import { Button, EmptyState } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useClinicSwitch, openClinicBooking } from '../../hooks/useClinicSwitch';

// The empty day, told according to who this doctor is at their clinic (spec §4,
// «پزشک متصل به کلینیک ولی بدون نوبت»):
//
//   • owns the clinic   → «ثبت نوبت», which opens the clinic panel's booking modal
//                         in a new tab (useClinicSwitch.openClinicBooking) — no
//                         search, no extra steps.
//   • is only a member  → they cannot book at someone else's desk, so say so, say
//                         whom to ask, and offer «مشاهده اطلاعات کلینیک» (the
//                         settings «مطب‌ها» section) instead of a dead end.
//   • has no clinic     → never reaches here. «نوبتی ثبت نشده» would be true of
//                         every day that doctor will ever open and says nothing
//                         about why, so پذیرش renders `NoClinicGuide` in this
//                         slot instead (both surfaces decide before calling us,
//                         because only they know whether the slot wants a card
//                         around it).
//
// While the lookup is in flight nothing extra is shown: the button appears once,
// when we know, rather than flickering in. Both surfaces (web card, mobile list)
// render this; only `title`, `description` and the icon differ.
//
// Since the doctor app books by the clinic's grant (`onBook` + `canBook`, from
// `myBookingClinics`), «ثبت نوبت» opens the in-app booking for an owner AND an
// active member alike — where the grant is withdrawn the booking waits for the
// clinic, but it is still the doctor's to make. The branches below remain for a
// surface that does not pass `onBook`, and for a doctor with no bookable clinic.
export default function NoAppointments({ icon, title, description, doctorId, navigate, onBook, canBook = false, ...rest }) {
  const { t } = useTranslation();
  const { loading, ownedClinic, joinedClinic } = useClinicSwitch();

  let body = description;
  let action = null;

  if (onBook && canBook) {
    action = (
      <Button size="sm" data-testid="reception-empty-book" onClick={onBook}>
        {t('dashboard.empty.book')}
      </Button>
    );
  } else if (!loading && ownedClinic) {
    action = (
      <Button
        size="sm"
        data-testid="reception-empty-book"
        onClick={() => openClinicBooking(ownedClinic, doctorId)}
      >
        {t('dashboard.empty.book')}
      </Button>
    );
  } else if (!loading && joinedClinic) {
    body = (
      <>
        {description && <div>{description}</div>}
        <div data-testid="reception-empty-ask-admin" style={description ? { marginTop: 6 } : undefined}>
          {t('dashboard.empty.askClinicAdmin')}
        </div>
      </>
    );
    action = (
      <Button
        size="sm"
        variant="outline"
        data-testid="reception-empty-view-clinic"
        onClick={() => navigate?.('profile', { tab: 'clinics' })}
      >
        {t('dashboard.empty.viewClinic')}
      </Button>
    );
  }

  return <EmptyState {...rest} icon={icon} title={title} description={body} action={action} />;
}
